import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * PHI/PII fields that should be sanitized from email content
 * These fields will be masked or removed before sending via email
 */
const SENSITIVE_FIELDS = [
  'patientID',
  'patientId',
  'patientNames',
  'patientName',
  'ssn',
  'dateOfBirth',
  'dob',
  'address',
  'phone',
  'mobile',
  'medicalLicense',
  'deaCertification',
  'npiNumber',
  'emergencies',
  'treatmentOptions',
  'incidentDescription',
  'medicalHistory',
  'diagnosis',
  'symptoms',
  'medications',
  'allergies'
];

/**
 * Sanitizes form data by removing or masking PHI/PII fields
 * @param {object} data - The form data to sanitize
 * @returns {object} - Sanitized form data safe for email
 */
function sanitizeFormData(data) {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  const sanitized = {};
  
  for (const [key, value] of Object.entries(data)) {
    // Check if this is a sensitive field
    const isSensitive = SENSITIVE_FIELDS.some(field => 
      key.toLowerCase().includes(field.toLowerCase())
    );
    
    if (isSensitive) {
      // Mask sensitive values
      if (typeof value === 'string' && value.length > 0) {
        // Show only field reference, not actual value
        sanitized[key] = '[PROTECTED - View in System]';
      } else if (Array.isArray(value) && value.length > 0) {
        sanitized[key] = `[${value.length} items - View in System]`;
      } else {
        sanitized[key] = '[PROTECTED]';
      }
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeFormData(value);
    } else {
      // Keep non-sensitive values
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Creates a sanitized summary for email content
 * @param {object} formData - The original form data
 * @param {string} category - The form category
 * @returns {object} - Object with sanitized text and html content
 */
function createSanitizedEmailContent(formData, category) {
  const sanitizedData = sanitizeFormData(formData);
  
  // Extract only safe fields for email
  const submissionDate = sanitizedData.submittedAt 
    ? new Date(sanitizedData.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  
  const safeFields = {
    practice: sanitizedData.practice || 'Not specified',
    userEmail: sanitizedData.userEmail || 'Not specified',
    submissionDate: submissionDate,
    category: category
  };
  
  const text = `New ${category} Form Submission

Practice: ${safeFields.practice}
Submitted By: ${safeFields.userEmail}
Date: ${safeFields.submissionDate}

⚠️ NOTICE: This email contains a notification only. 
For security and HIPAA compliance, detailed form data including any Protected Health Information (PHI) is NOT included in this email.

Please log in to the AlliedOMS system to view the complete submission details.`;

  const html = `
    <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 600px;">
      <h2 style="color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px;">
        New ${category} Form Submission
      </h2>
      
      <div style="background-color: #f0f9ff; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #1e40af;">
        <p style="margin: 5px 0;"><strong>Practice:</strong> ${safeFields.practice}</p>
        <p style="margin: 5px 0;"><strong>Submitted By:</strong> ${safeFields.userEmail}</p>
        <p style="margin: 5px 0;"><strong>Date:</strong> ${safeFields.submissionDate}</p>
      </div>
      
      <div style="background-color: #fef3c7; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #f59e0b;">
        <p style="margin: 0; color: #92400e;">
          <strong>⚠️ Security Notice:</strong><br/>
          For HIPAA compliance and data protection, detailed form data including any Protected Health Information (PHI) is NOT included in email notifications.
        </p>
      </div>
      
      <div style="background-color: #f3f4f6; padding: 15px; border-radius: 8px; text-align: center;">
        <p style="margin: 0 0 10px 0; color: #374151;">
          Please log in to the AlliedOMS system to view the complete submission details.
        </p>
      </div>
      
      <p style="font-size: 12px; color: #6b7280; margin-top: 20px; text-align: center;">
        This is an automated notification. Do not reply to this email.
      </p>
    </div>
  `;
  
  return { text, html };
}

/**
 * Queue an email to be sent for a specific form category
 * The Cloud Function will process this and create the mail document
 * which the Firebase Extension will use to send the email
 * 
 * SECURITY: PHI/PII is automatically sanitized from email content.
 * Only notification metadata is sent via email - full data must be viewed in the system.
 * 
 * @param {string} category - The form category (e.g., 'complaints', 'hipaa', 'complications')
 * @param {object} formData - The form submission data
 * @param {object} options - Optional email configuration
 * @param {string} options.subject - Custom email subject (default: auto-generated)
 * @param {string} options.text - Plain text email body (default: sanitized content)
 * @param {string} options.html - HTML email body (default: sanitized content)
 * @param {string} options.replyTo - Reply-to email address
 * @param {string} options.subCategory - Subcategory for HIPAA or IT Breach forms
 * @param {string} options.formId - Specific form ID for form-level routing
 * @param {boolean} options.includeSanitizedData - If true, includes sanitized (masked) form data
 * @returns {Promise<string>} - The ID of the created mailQueue document
 */
export async function queueEmail(category, formData, options = {}) {
  try {
    // Handle subcategories (e.g., HIPAA subcategories, IT Breach subcategories)
    let finalCategory = category;
    let formId = options.formId || null;
    
    if (options.subCategory) {
      if (category === 'hipaa') {
        finalCategory = `hipaa_${options.subCategory}`;
        formId = formId || options.subCategory;
      } else if (category === 'itBreach') {
        finalCategory = `itBreach_${options.subCategory}`;
        formId = formId || options.subCategory;
      }
    }

    // Generate sanitized email content (no PHI/PII)
    const sanitizedContent = createSanitizedEmailContent(formData, category);

    // Prepare email payload - NEVER include raw form data
    const emailPayload = {
      subject: options.subject || `New ${category} form submission`,
      // Use provided content or fall back to sanitized version
      text: options.text ? sanitizeEmailText(options.text) : sanitizedContent.text,
      html: options.html ? sanitizeEmailHtml(options.html) : sanitizedContent.html,
      // Store only safe reference metadata (no PHI)
      metadata: {
        category: finalCategory,
        practice: formData.practice || 'Unknown',
        submittedAt: new Date().toISOString(),
        // Do NOT include formData here
      },
      ...(options.replyTo && { replyTo: options.replyTo })
    };

    // Add to mailQueue collection
    // The Cloud Function will process this and create the mail document
    const queueRef = await addDoc(collection(db, 'mailQueue'), {
      category: finalCategory,
      formId: formId, // For form-level routing
      organization: formData.organization || null, // Include organization for multi-tenancy
      payload: emailPayload,
      createdAt: serverTimestamp(),
      status: 'pending'
    });

    console.log(`Email queued for category: ${finalCategory}, formId: ${formId}`, queueRef.id);
    return queueRef.id;

  } catch (error) {
    console.error('Error queueing email:', error);
    throw error;
  }
}

/**
 * Sanitizes plain text email content by removing potential PHI patterns
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text
 */
function sanitizeEmailText(text) {
  if (!text) return text;
  
  // Remove JSON data blocks that might contain PHI
  let sanitized = text.replace(/Form Data:[\s\S]*?(?=\n\n|$)/gi, 
    'Form Data: [Protected - View in System]\n\n');
  
  // Remove patient ID patterns
  sanitized = sanitized.replace(/patient\s*id[:\s]+\S+/gi, 'Patient ID: [Protected]');
  
  return sanitized;
}

/**
 * Sanitizes HTML email content by removing potential PHI patterns
 * @param {string} html - The HTML to sanitize
 * @returns {string} - Sanitized HTML
 */
function sanitizeEmailHtml(html) {
  if (!html) return html;
  
  // Remove <pre> blocks that typically contain form data dumps
  let sanitized = html.replace(/<pre[^>]*>[\s\S]*?<\/pre>/gi, 
    '<p style="color: #6b7280; font-style: italic;">[Form data protected - View in System]</p>');
  
  return sanitized;
}

/**
 * Helper function to determine category from form type
 * Useful when you have form type strings and need to map them to categories
 * Returns: { category, subCategory?, formId }
 */
export function getCategoryFromFormType(formType) {
  const categoryMap = {
    // Patient Complaints
    'Complaint - Clinical Care': { category: 'complaints', formId: 'clinicalCare' },
    'Complaint - Customer Service': { category: 'complaints', formId: 'customerService' },
    'Complaint - Billing/Financial': { category: 'complaints', formId: 'billingFinancial' },
    'Complaint - Facility/Environment': { category: 'complaints', formId: 'facilityEnvironment' },
    'ComplaintForm': { category: 'complaints', formId: 'clinicalCare' }, // Legacy
    
    // Complications
    'Anesthesia': { category: 'complications', formId: 'anesthesia' },
    'Dental Implant': { category: 'complications', formId: 'dentalImplant' },
    'Dentoalveolar Surgery': { category: 'complications', formId: 'dentoalveolarSurgery' },
    'Needle Stick Injury': { category: 'complications', formId: 'needleStickInjury' },
    'ComplicationsForm': { category: 'complications', formId: 'anesthesia' }, // Legacy
    
    // HIPAA subcategories
    'unauthorizedDisclosureForm': { category: 'hipaa', subCategory: 'unauthorizedDisclosure', formId: 'unauthorizedDisclosure' },
    'improperAccessForm': { category: 'hipaa', subCategory: 'improperAccess', formId: 'improperAccess' },
    'lostRecordsForm': { category: 'hipaa', subCategory: 'lostRecords', formId: 'lostRecords' },
    'vendorBreachForm': { category: 'hipaa', subCategory: 'vendorBreach', formId: 'vendorBreach' },
    'patientRightsForm': { category: 'hipaa', subCategory: 'patientRights', formId: 'patientRights' },
    'transmissionErrorForm': { category: 'hipaa', subCategory: 'transmissionError', formId: 'transmissionError' },
    
    // IT Security Events
    'dataBreachForm': { category: 'itBreach', subCategory: 'dataEvent', formId: 'dataEvent' },
    'ransomwareForm': { category: 'itBreach', subCategory: 'ransomwareEvent', formId: 'ransomwareEvent' },
    'cyberAttackForm': { category: 'itBreach', subCategory: 'cyberEvent', formId: 'cyberEvent' },
    'systemBreachForm': { category: 'itBreach', subCategory: 'systemEvent', formId: 'systemEvent' },
    'emailBreachForm': { category: 'itBreach', subCategory: 'emailEvent', formId: 'emailEvent' },
    'deviceBreachForm': { category: 'itBreach', subCategory: 'deviceEvent', formId: 'deviceEvent' },
    // Legacy IT Breach mappings
    'CyberAttackForm': { category: 'itBreach', subCategory: 'cyberEvent', formId: 'cyberEvent' },
    'DataBreachForm': { category: 'itBreach', subCategory: 'dataEvent', formId: 'dataEvent' },
    'EmailBreachForm': { category: 'itBreach', subCategory: 'emailEvent', formId: 'emailEvent' },
    'DeviceBreachForm': { category: 'itBreach', subCategory: 'deviceEvent', formId: 'deviceEvent' },
    'SystemBreachForm': { category: 'itBreach', subCategory: 'systemEvent', formId: 'systemEvent' },
    'RansomwareForm': { category: 'itBreach', subCategory: 'ransomwareEvent', formId: 'ransomwareEvent' },
    
    // Equipment Malfunction
    'Equipment - Surgical': { category: 'equipment', formId: 'surgical' },
    'Equipment - Imaging': { category: 'equipment', formId: 'imaging' },
    'Equipment - Sterilization': { category: 'equipment', formId: 'sterilization' },
    'Equipment - Anesthesia': { category: 'equipment', formId: 'anesthesiaEquipment' },
    'Equipment - Computer/Software': { category: 'equipment', formId: 'computerSoftware' },
    'Equipment - Facility Systems': { category: 'equipment', formId: 'facilitySystems' },
    'EquipmentForm': { category: 'equipment', formId: 'surgical' }, // Legacy
    
    // Scheduling/Communication
    'Scheduling - Appointment': { category: 'scheduling', formId: 'appointment' },
    'Scheduling - Communication': { category: 'scheduling', formId: 'patientCommunication' },
    'Scheduling - Documentation': { category: 'scheduling', formId: 'documentation' },
    'Scheduling - System/Process': { category: 'scheduling', formId: 'systemProcess' },
    'SchedulingForm': { category: 'scheduling', formId: 'appointment' }, // Legacy
    
    // Emergency
    'emergency': { category: 'emergency', formId: 'emergency' },
    
    // Aggressive Patient
    'AggressivePatientForm': { category: 'aggressivePatient', formId: 'aggressivePatient' }
  };

  return categoryMap[formType] || { category: 'scheduling', formId: 'appointment' }; // Default
}

/**
 * Convenience function to send email after form submission
 * Automatically determines category from form type
 */
export async function sendFormSubmissionEmail(formType, formData, customOptions = {}) {
  const categoryInfo = getCategoryFromFormType(formType);
  
  const { category, subCategory, formId } = categoryInfo;

  return await queueEmail(category, formData, {
    ...customOptions,
    ...(subCategory && { subCategory }),
    ...(formId && { formId })
  });
}
